/**
 * Extract plain text from PDF / DOCX / TXT files for Elsa-Aligned QA.
 * Runs entirely client-side — no upload to server.
 */
import mammoth from "mammoth";

export type ExtractedDoc = {
  text: string;
  pageCount?: number;
  charCount: number;
  fileName: string;
  mimeType: string;
};

const MAX_BYTES = 25 * 1024 * 1024; // 25 MB

export async function extractDocumentText(file: File): Promise<ExtractedDoc> {
  if (file.size > MAX_BYTES) {
    throw new Error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 25 MB.`);
  }

  const name = file.name.toLowerCase();
  const mime = file.type || "";

  // PDF
  if (mime === "application/pdf" || name.endsWith(".pdf")) {
    const text = await extractPdfText(file);
    return { text, charCount: text.length, fileName: file.name, mimeType: mime || "application/pdf" };
  }

  // DOCX
  if (
    name.endsWith(".docx") ||
    mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ) {
    const buf = await file.arrayBuffer();
    const { value } = await mammoth.extractRawText({ arrayBuffer: buf });
    return { text: value, charCount: value.length, fileName: file.name, mimeType: mime };
  }

  // Plain text / markdown
  if (mime.startsWith("text/") || name.endsWith(".txt") || name.endsWith(".md")) {
    const text = await file.text();
    return { text, charCount: text.length, fileName: file.name, mimeType: mime || "text/plain" };
  }

  throw new Error("Unsupported file type. Upload PDF, DOCX, or TXT.");
}

async function extractPdfText(file: File): Promise<string> {
  // Lazy-load pdfjs to keep main bundle small
  const pdfjs: any = await import("pdfjs-dist");
  // Worker via CDN matching installed version
  const version = pdfjs.version || "5.7.284";
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${version}/build/pdf.worker.min.mjs`;

  const data = new Uint8Array(await file.arrayBuffer());
  const pdf = await pdfjs.getDocument({ data }).promise;
  const out: string[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const pageText = content.items.map((it: any) => ("str" in it ? it.str : "")).join(" ");
    out.push(`--- PAGE ${i} ---\n${pageText}`);
  }
  return out.join("\n\n").replace(/\s+\n/g, "\n").trim();
}
